import { types, flow, applySnapshot } from "mobx-state-tree";
import { v4 as uuidv4 } from "uuid";

import { format_date } from "../utils/formatDate";

import LocationStore from "./LocationStore";
import MatrixStore from "./MatrixStore";
import {
  Client,
  Outlet,
  Product,
  Employee,
  Team,
  SubTeam,
  ReportMerch,
  ReportAmountOutlets,
  Kind,
  Region,
} from "./common";
import Route from "./Route";
import { URL_API } from "./globals";
import RepostSilpo from "./RepostSilpo";
import ReportSalary from "./RepostSalary";

const Merch = types.model({
  merch: types.reference(Employee),
  start_time: types.number,
  end_time: types.number,
});

const TeamUser = types
  .model({
    uuid: types.optional(types.identifier, () => uuidv4()),
    team: types.reference(Team),
    checked: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setChecked: () => {
      self.checked = !self.checked;
    },
  }));

const SubTeamUser = types
  .model({
    uuid: types.optional(types.identifier, () => uuidv4()),
    subteam: types.reference(SubTeam),
    checked: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setChecked: () => {
      self.checked = !self.checked;
    },
  }));

const RegionUser = types
  .model({
    uuid: types.optional(types.identifier, () => uuidv4()),
    region: types.reference(Region),
    checked: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setChecked: () => {
      self.checked = !self.checked;
    },
  }));

const User = types.model({
  uuid: types.identifier,
  username: types.string,
  password: types.string,
  mode: types.boolean,
  admin: types.optional(types.string, "merchandiser"),
  teams: types.optional(types.array(types.string), []),
  subteams: types.optional(types.array(types.string), []),
  regions: types.optional(types.array(types.string), []),
});

const PhotoAll = types.model({
  uuid: types.optional(types.identifier, () => uuidv4()),
  date: types.optional(types.string, ""),
  image: types.optional(types.string, ""),
});

const ProductByOutlet = types.model({
  uuid: types.optional(types.identifier, () => uuidv4()),
  product: types.reference(Product),
  action: types.optional(types.integer, 0),
});

const ItemOrder = types
  .model({
    uuid: types.optional(types.identifier, () => uuidv4()),
    product: types.reference(Product),
    count: types.optional(types.integer, 0),
    balance: types.optional(types.integer, 0),
  })
  .actions((self) => ({
    set(row) {
      applySnapshot(self, row);
    },
  }));

const Order = types.model({
  uuid: types.optional(types.identifier, () => uuidv4()),
  emploee: types.optional(types.maybeNull(types.reference(Employee)), null),
  date: types.optional(types.string, ""),
  outlet: types.reference(Outlet),
  items: types.optional(types.array(ItemOrder), []),
  createat: types.optional(types.string, ""),
});

const OrderStore = types
  .model({
    date: types.optional(types.string, ""),
    curClient: types.optional(types.maybeNull(types.reference(Client)), null),
    curRegion: types.optional(types.maybeNull(types.reference(Region)), null),
    curOutlet: types.optional(types.maybeNull(types.reference(Outlet)), null),
    curOrder: types.optional(types.maybeNull(types.reference(Order)), null),
    orders: types.optional(types.array(Order), []),
  })
  .actions((self) => ({
    setDate: flow(function* (date) {
      yield self.getOrders(date);
      self.date = date;
      self.curClient = null;
      self.curRegion = null;
      self.curOutlet = null;
      self.curOrder = null;
    }),
    setCurClient(client) {
      self.curClient = client;
    },
    delCurClient() {
      self.curClient = null;
    },
    setCurRegion(region) {
      self.curRegion = region;
    },
    delCurRegion() {
      self.curRegion = null;
    },
    setCurOutlet(outlet) {
      self.curOutlet = outlet;
    },
    delCurOutlet() {
      self.curOutlet = null;
    },
    setCurOrder(order) {
      self.curClient = order.outlet.client;
      self.curRegion = order.outlet.region;
      self.curOutlet = order.outlet;
      self.curOrder = order;
    },
    delCurOrder() {
      self.curOrder = null;
    },
    saveOrder: flow(function* (value) {
      try {
        if (self.curOrder) {
          const response = yield fetch(`${URL_API}/api/saveorder`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: { order: self.curOrder },
            }),
          });
          const json = yield response.json();
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    removeOrder: flow(function* (value) {
      try {
        if (self.curOrder) {
          const response = yield fetch(`${URL_API}/api/removeorder`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: { order: self.curOrder },
            }),
          });
          const json = yield response.json();
          self.curOrder = null;
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getOrders: flow(function* (date) {
      try {
        const response = yield fetch(`${URL_API}/api/getorders`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: { date },
          }),
        });

        const json = yield response.json();
        self.orders.clear();

        console.log(json.data);

        for (const item of json.data) {
          self.orders.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getOrder: flow(function* (value) {
      try {
        const response = yield fetch(`${URL_API}/api/georder`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: { order: self.curOrder },
          }),
        });
        const json = yield response.json();
        self.orders.remove(self.curOrder);
        const { order } = json.data;

        self.orders.push(order);
        self.curOrder = self.orders[self.orders.length - 1];
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getOrderByOutlet: flow(function* () {
      try {
        if (self.curOutlet && self.date) {
          const response = yield fetch(`${URL_API}/api/getorderbyoutlet`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: {
                outlet: self.curOutlet.uuid,
                date: self.date,
                employee: self.curOutlet.employee,
              },
            }),
          });
          const json = yield response.json();
          const order = json.data;

          const curOrder = self.orders.find((it) => it.uuid === order.uuid);
          if (curOrder) {
            self.orders.remove(curOrder);
          }

          self.orders.push(order);
          self.curOrder = self.orders[self.orders.length - 1];
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
  }));

const store = types
  .model({
    locationStore: types.optional(LocationStore, {
      locations: [],
    }),
    orderStore: types.optional(OrderStore, {
      orders: [],
    }),
    matrixStore: types.optional(MatrixStore, {
      matrix: [],
      outlets: [],
      products: [],
    }),
    orders: types.optional(types.array(Order), []),
    reportSilpo: types.optional(RepostSilpo, {
      date1: format_date(Date.now()),
      date2: format_date(Date.now()),
      client: null,
    }),
    reportSalary: types.optional(ReportSalary, {
      date1: format_date(Date.now()),
      date2: format_date(Date.now()),
    }),
    productsByOutlet: types.optional(types.array(ProductByOutlet), []),
    regions: types.optional(types.array(Region), []),
    filterRegions: types.optional(types.array(types.string), []),
    teams: types.optional(types.array(Team), []),
    subteams: types.optional(types.array(SubTeam), []),
    clients: types.optional(types.array(Client), []),
    outlets: types.optional(types.array(Outlet), []),
    products: types.optional(types.array(Product), []),
    employees: types.optional(types.array(Employee), []),
    photoes: types.optional(types.array(types.string), []),
    photoesAll: types.optional(types.array(PhotoAll), []),
    route: types.optional(types.maybeNull(Route), null),
    merchs: types.optional(types.array(Merch), []),
    kinds: types.optional(types.array(Kind), []),
    reportmerch: types.optional(types.array(ReportMerch), []),
    reportAmountOutlets: types.optional(types.array(ReportAmountOutlets), []),
    login: types.optional(types.string, ""),
    password: types.optional(types.string, ""),
    isAuth: types.optional(types.boolean, false),
    users: types.optional(types.array(User), []),
    currentUuid: types.optional(types.maybeNull(types.string), null),
    currentUserName: types.optional(types.string, ""),
    currentPassword: types.optional(types.string, ""),
    currentMode: types.optional(types.boolean, false),
    teamsUser: types.optional(types.array(TeamUser), []),
    subteamsUser: types.optional(types.array(SubTeamUser), []),
    regionUser: types.optional(types.array(RegionUser), []),
    currentAdmin: types.optional(types.string, "merchandiser"),
    admin: types.optional(types.string, "merchandiser"),
    date1Photo: types.optional(types.string, ""),
    date2Photo: types.optional(types.string, ""),
    outletPhoto: types.optional(types.maybeNull(types.reference(Outlet)), null),
    clientPhoto: types.optional(types.maybeNull(types.reference(Client)), null),
    regionPhoto: types.optional(types.maybeNull(types.reference(Region)), null),
  })
  .actions((self) => ({
    getPhotoAll: flow(function* (value) {
      try {
        if (self.outletPhoto) {
          const outlet = self.outletPhoto.uuid;

          const response = yield fetch(`${URL_API}/api/getphotoall`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: {
                outlet,
                date1: self.date1Photo,
                date2: self.date2Photo,
              },
            }),
          });
          const json = yield response.json();

          self.photoesAll.clear();

          for (const item of json.data) {
            self.photoesAll.push({ image: item.id, date: item.date });
          }
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getProductsByOutlet: flow(function* (value) {
      try {
        if (self.outletPhoto) {
          const outlet = self.outletPhoto.uuid;

          const response = yield fetch(`${URL_API}/api/getproductsbyoutlet`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: {
                outlet,
              },
            }),
          });
          const json = yield response.json();

          self.productsByOutlet.clear();
          for (const item of json.data) {
            self.productsByOutlet.push(item);
          }
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    setDate1Photo(date1) {
      self.date1Photo = date1;
    },
    setDate2Photo(date2) {
      self.date2Photo = date2;
    },
    setOutletPhoto(outletPhoto) {
      self.outletPhoto = outletPhoto;
    },
    setClientPhoto(clientPhoto) {
      self.clientPhoto = clientPhoto;
    },
    setRegionPhoto(regionPhoto) {
      self.regionPhoto = regionPhoto;
    },
    delRegionPhoto() {
      self.regionPhoto = null;
      self.outletPhoto = null;
      self.photoesAll.clear();
      self.productsByOutlet.clear();
    },
    delOutletPhoto() {
      self.outletPhoto = null;
      self.photoesAll.clear();
      self.productsByOutlet.clear();
    },
    delClientPhoto() {
      self.clientPhoto = null;
      self.regionPhoto = null;
      self.outletPhoto = null;
      self.photoesAll.clear();
      self.productsByOutlet.clear();
    },
    setAdmin(admin = "merchandiser") {
      self.admin = admin;
    },
    setCurrentAdmin(admin = "merchandiser") {
      self.currentAdmin = admin;
    },
    setLogin(login) {
      self.login = login;
    },
    setPassword(password) {
      self.password = password;
    },
    setCurrentUserName(currentUserName) {
      self.currentUserName = currentUserName;
    },
    setCurrentPassword(currentPassword) {
      self.currentPassword = currentPassword;
    },
    setCurrent({ uuid, username, password, mode, admin }) {
      // console.log(admin)
      self.currentUuid = uuid;
      self.currentUserName = username;
      self.currentPassword = password;
      self.setCurrentMode(mode);
      self.setRegionUser();
      self.setCurrentAdmin(admin);
    },
    clearCurrent() {
      self.currentUuid = "";
      self.currentUserName = "";
      self.currentPassword = "";
      self.currentMode = false;
      self.teamsUser = [];
      self.subteamsUser = [];
      self.regionUser = [];
    },
    setCurrentMode(mode = null) {
      if (mode !== null) {
        self.currentMode = mode;
      } else {
        self.currentMode = !self.currentMode;
      }

      if (self.currentMode) {
        self.teamsUser.clear();
        for (const it of self.teams) {
          const isArray =
            self.users.filter(
              (it2) =>
                it2.uuid === self.currentUuid &&
                it2.teams.filter((it3) => it3 === it.uuid).length > 0
            ).length > 0;
          self.teamsUser.push({ team: it.uuid, checked: isArray });
        }
      } else {
        self.subteamsUser.clear();
        for (const it of self.subteams) {
          const isArray =
            self.users.filter(
              (it2) =>
                it2.uuid === self.currentUuid &&
                it2.subteams.filter((it3) => it3 === it.uuid).length > 0
            ).length > 0;
          self.subteamsUser.push({ subteam: it.uuid, checked: isArray });
        }
      }
    },
    setRegionUser() {
      self.regionUser.clear();

      for (const it of self.regions) {
        const isArray =
          self.users.filter(
            (it2) =>
              it2.uuid === self.currentUuid &&
              it2.regions.filter((it3) => it3 === it.uuid).length > 0
          ).length > 0;

        self.regionUser.push({ region: it.uuid, checked: isArray });
      }
    },
    getFetchTeams: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getteams`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.teams.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchKinds: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getkinds`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.kinds.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchRegions: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getregions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        self.regions.clear();
        const json = yield response.json();
        for (const item of json.data) {
          self.regions.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchSubteams: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getsubteams`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.subteams.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchClients: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getclients`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();

        for (const item of json.data) {
          self.clients.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchOutlets: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getoutlets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();

        self.outlets.clear();
        for (const item of json.data) {
          try {
            self.outlets.push(item);
          } catch {}
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchProducts: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getproducts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.products.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchEmployees: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getemployees`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.employees.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchPhotoes: flow(function* (uuid) {
      try {
        const response = yield fetch(`${URL_API}/api/getphoto`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ data: { uuid } }),
        });
        const json = yield response.json();
        self.photoes.clear();
        for (const item of json.images) {
          self.photoes.push(item.image);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getFetchRoutes: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getroutes`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.routes.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getPeriod: flow(function* (date1, date2) {
      try {
        const response = yield fetch(`${URL_API}/api/getperiod`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            date1,
            date2,
          }),
        });

        const json = yield response.json();
        self.reportmerch.clear();
        for (const item of json.data) {
          self.reportmerch.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getPeriodOutlets: flow(function* (date) {
      try {
        const response = yield fetch(`${URL_API}/api/getperiodoutlets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            date,
          }),
        });

        const json = yield response.json();

        self.reportAmountOutlets.clear();
        for (const item of json.data) {
          self.reportAmountOutlets.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    saveRoute: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/saveroute`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: self.outlets.filter((item) => item.mod),
          }),
        });
        const json = yield response.json();
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    saveOutlets: flow(function* (outlets) {
      try {
        const arr = [];
        for (const iter of outlets) {
          const outlet = self.outlets.find((item) => item.code === iter.outlet);
          const merchandiser = self.employees.find(
            (item) => item.code === iter.merchandiser
          );
          if (outlet && merchandiser) {
            arr.push({ outlet: outlet.uuid, merchandiser: merchandiser.uuid });
          }
        }

        yield fetch(`${URL_API}/api/saveoutlets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: arr,
          }),
        });
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getVisits: flow(function* (merch, date) {
      try {
        const response = yield fetch(`${URL_API}/api/getvisits`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ data: { merch, date } }),
        });
        const json = yield response.json();
        const {
          amount_hours = 0,
          amount_outlets = 0,
          comment = "",
          visits = [],
        } = json.data;

        self.route = {
          date,
          amount_hours,
          amount_outlets,
          comment,
          visits: visits.map((visit) => ({
            id: visit.uuid,
            outlet: visit.outlet,
            start_time: visit.start_time,
            end_time: visit.end_time,
            start_latitude: visit.start_latitude,
            end_latitude: visit.end_latitude,
            start_longitude: visit.start_longitude,
            end_longitude: visit.end_longitude,
            actions: visit.actions,
            products: visit.products.map((product) => ({
              id: product.uuid,
              product: product.product,
              count: product.count,
              balance: product.balance,
              store: product.store,
              isPresent: product.ispresent,
              possibility: product.possibility,
              defect: product.defect,
            })),
          })),
        };
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getMerchs: flow(function* (team, date) {
      try {
        const response = yield fetch(`${URL_API}/api/getmerchs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ data: { team, date } }),
        });
        const json = yield response.json();
        self.merchs = json.data;
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getMerchReport: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getmerchreport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        for (const item of json.data) {
          self.reportmerch.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    auth: flow(function* () {
      try {
        const formData = new URLSearchParams();
        formData.append("username", self.login);
        formData.append("password", self.password);

        const response = yield fetch(`${URL_API}/api/login`, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          method: "POST",
          body: formData.toString(),
          json: true,
        });
        const json = yield response.json();
        if (json.status && json.status === "ok") {
          self.isAuth = true;
          self.getFetchTeams();
          self.getFetchSubteams();
          self.getFetchRegions();
          self.getFetchKinds();
          self.getFetchClients();
          self.getFetchOutlets();
          self.getFetchProducts();
          self.getFetchEmployees();
          json.role && self.setAdmin(json.role);
          for (const it of json.regions) {
            self.filterRegions.push(it.region);
          }
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    getUsers: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/getusers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = yield response.json();
        self.users.clear();
        for (const item of json.data) {
          self.users.push(item);
        }
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
    setUser: flow(function* () {
      try {
        const response = yield fetch(`${URL_API}/api/setuser`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uuid: self.currentUuid,
            username: self.currentUserName,
            password: self.currentPassword,
            mode: self.currentMode,
            admin: self.currentAdmin,
            teams: self.teamsUser
              .filter((it) => it.checked)
              .map(({ uuid, team }) => ({
                uuid,
                team: team.uuid,
              })),
            subteams: self.subteamsUser
              .filter((it) => it.checked)
              .map(({ uuid, subteam }) => ({
                uuid,
                subteam: subteam.uuid,
              })),
            regions: self.regionUser
              .filter((it) => it.checked)
              .map(({ uuid, region }) => ({
                uuid,
                region: region.uuid,
              })),
          }),
        });
        const json = yield response.json();

        yield self.getUsers();
        self.clearCurrent();
      } catch (error) {
        console.error("Ошибка:", error);
      }
    }),
  }))
  .create();

export default store;
